import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '@app/hooks';
import useCalculateGATiming from '../hooks/useCalculateGATiming';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { KitIcon, AlloyButton } from 'cat-ecommerce-alloy';
import ItemsInOrderTable from './ItemsInOrderTable';
import ItemsInOrderTableDecriptorTag from './ItemsInOrderTable/ItemsInOrderTableDescriptorTag';
import PreferenceToggles from '../shopping-cart/OrderItems/OrderItemsTable/common/PreferenceToggles';
import { getInitialImgSettings } from '@app/components/pages/checkout/shopping-cart/OrderItems/OrderItemsTable/CartItemsTable/utils';
import { getSessionStorage, setSessionStorage } from '@app/utils';
import {
  AVAIL_BY_DATE,
  AVAIL_BY_SOURCE,
  PAGE_BREAKPOINTS,
  USER_TYPE_GUEST
} from '@app/constants/commonConstants';
import declarations from '../PickUpAndDelivery/declarations.proptypes';
import { CHECKOUT_INITIAL_TIME } from '../shopping-cart/ShoppingCartPage/constants';
import {
  PROCEED_TO_CHECKOUT,
  PICKUP_AND_DELIVERY_PAGE
} from '@app/constants/analyticsConstants';
import {
  PCC_PS_ITEMS_OPTIMIZATION,
  PCC_PUD_TITLE_UPDATES
} from '@app/constants/featureFlags';
import cx from 'classnames';
import stylesModule from '../../summary-and-payment/styles.module.scss';

const ItemsInOrder = ({
  isPendingQuote,
  currencyNotation,
  showGST,
  includeGST,
  taxDisclaimerMessage,
  items = [],
  isQuantityMergeEnabled,
  showDagger = false,
  showOptimizationChanges = false,
  titleUpdatesActive = false
}) => {
  const [t] = useTranslation();
  const userType = useSelector(s => s?.common?.userType);
  const isGuest = userType === USER_TYPE_GUEST;
  const titleText = isPendingQuote
    ? t('ITEMS_IN_QUOTE')
    : t('2691_ITEMS_IN_ORDER');
  const orderHasServiceOptionInstruction = !!items.find(
    item => !!item?.orderItemExtendAttribute?.selfServiceOptionInstructionLink
  );

  const showAvailabilityBySource = useSelector(
    state =>
      !!state?.shoppingPreferences?.shoppingPreferencesData
        ?.showSourcingDetailToggle
  );

  const key = showAvailabilityBySource ? AVAIL_BY_DATE : AVAIL_BY_SOURCE;
  const [isToggleDetails, setIsToggleDetails] = useState(
    getSessionStorage(key) === 'true'
  );

  const handleAvailabilityToggle = checked => {
    setSessionStorage(key, checked);
    setIsToggleDetails(checked);
  };

  const {
    shoppingPreferencesData: { imageSettings = [] }
  } = useSelector(s => s.shoppingPreferences);

  const itemsInOrderOptimizationFlag = useSelector(
    s => s.featureFlag[PCC_PS_ITEMS_OPTIMIZATION]
  );

  const pUDTitleUpdatesFlag = useSelector(
    s => s.featureFlag[PCC_PUD_TITLE_UPDATES]
  );

  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const itemCount =
    items.reduce((acc, { quantity }) => acc + parseInt(quantity), 0) ?? 0;

  useCalculateGATiming(
    CHECKOUT_INITIAL_TIME,
    PROCEED_TO_CHECKOUT,
    PICKUP_AND_DELIVERY_PAGE
  );

  const limit = 3;
  const [currentIndex, setCurrentIndex] = useState(limit);
  const [showMoreButton, setShowMoreButton] = useState(items.length > limit);
  const [paginatedItems, setPaginatedItems] = useState(items.slice(0, limit));

  const showMore = index => {
    const endIndex = index + limit;
    const newShowMore = endIndex < items.length;
    setCurrentIndex(endIndex);
    setShowMoreButton(newShowMore);
    setPaginatedItems(items.slice(0, endIndex));
  };

  const initialImgSettings = getInitialImgSettings(imageSettings);
  const showOptimizationChangesMobile =
    showOptimizationChanges && itemsInOrderOptimizationFlag && !isMD;

  const itemsLength = items?.length;
  const paginatedItemsLength = paginatedItems?.length;

  useEffect(() => {
    if (itemsLength > 0 && paginatedItemsLength === 0) {
      showMore(0);
    }
  }, [itemsLength, paginatedItemsLength, showMore]);

  return (
    <section
      className={cx('w-100', stylesModule['mt-print-0'], {
        'form__section ': !showOptimizationChangesMobile,
        'mb-3': showOptimizationChangesMobile,
        'mt-3': !pUDTitleUpdatesFlag || !titleUpdatesActive,
        'mt-0 mt-md-3': titleUpdatesActive
      })}
    >
      <h4
        className={cx(
          'u-fs--20 mb-0 form__section-title h3 display-4',
          stylesModule['text-print-lg'],
          {
            'px-2': pUDTitleUpdatesFlag && !isMD,
            'text-capitalize': titleUpdatesActive && showOptimizationChanges
          }
        )}
      >
        {titleText} {showOptimizationChangesMobile && `(${itemCount})`}
      </h4>
      {orderHasServiceOptionInstruction && (
        <div className="pt-2">
          <ItemsInOrderTableDecriptorTag
            icon={KitIcon}
            iconClassName="mx-2 mt-0 u-fill--DarkGray"
            title={t('SSRO_INSTRUCTION_TEXT')}
          />
        </div>
      )}
      <div>
        {!isGuest && (
          <Formik
            initialValues={{
              displayOnScreen: true,
              displayOnPrint: true
            }}
          >
            {() => (
              <PreferenceToggles
                isShowNotesAndImageTogles={false}
                displayLineItemNotes={false}
                imageSettings={initialImgSettings}
                displayAvailabityAndSourcingDetails={isToggleDetails}
                setTogglePrefs={checked => handleAvailabilityToggle(checked)}
              />
            )}
          </Formik>
        )}
      </div>
      <div className={cx(stylesModule['mx-print-3'])}>
        <ItemsInOrderTable
          currencyNotation={currencyNotation}
          includeGST={includeGST}
          taxDisclaimerMessage={taxDisclaimerMessage}
          items={showOptimizationChangesMobile ? paginatedItems : items}
          showGST={showGST}
          isQuantityMergeEnabled={isQuantityMergeEnabled}
          displayAvailabityAndSourcingDetails={isToggleDetails}
          showGstDagger={showDagger}
        />
      </div>
      {showOptimizationChangesMobile && showMoreButton && (
        <div className="pt-3">
          <AlloyButton
            buttonType="secondary"
            size="large"
            className="w-100 w-lg-auto text-capitalize"
            onClick={() => showMore(currentIndex)}
          >
            {t('SHOW_MORE')}
          </AlloyButton>
        </div>
      )}
    </section>
  );
};

ItemsInOrder.propTypes = {
  isPendingQuote: PropTypes.bool,
  currencyNotation: PropTypes.string.isRequired,
  includeGST: PropTypes.bool,
  taxDisclaimerMessage: PropTypes.string,
  items: declarations.orderItems,
  showGST: PropTypes.bool,
  isQuantityMergeEnabled: PropTypes.bool,
  showDagger: PropTypes.bool,
  showOptimizationChanges: PropTypes.bool,
  titleUpdatesActive: PropTypes.bool
};

export default ItemsInOrder;
